import { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatMoney } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import {
  ClipboardList,
  Wallet,
  Timer,
  Package,
  Calendar as CalendarIcon,
  AlertTriangle,
  Receipt,
  XCircle,
  Truck,
  Coins,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { atrasoInfo } from "@/lib/atraso";

function Stat({ label, value, sub, icon: Icon, accent }) {
  return (
    <Card className="p-5 border-zinc-200 rounded-md bg-white hover:shadow-sm transition-transform duration-200 ease-out hover:-translate-y-px">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">{label}</div>
          <div className="mt-2 font-manrope font-black tracking-tight text-3xl">{value}</div>
          {sub && <div className="mt-1 text-xs text-zinc-500">{sub}</div>}
        </div>
        <div className={`w-9 h-9 rounded-md grid place-items-center ${accent}`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [extra, setExtra] = useState(null);
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [modo, setModo] = useState("actual"); // actual | historial
  const [cierres, setCierres] = useState([]);
  const [cierreId, setCierreId] = useState("");
  const [corte, setCorte] = useState(null); // {anteriores, despues, total}

  useEffect(() => {
    api.get("/cierres-mes").then(({ data }) => setCierres(data || [])).catch(() => {});
    api.get("/stats/pedidos-corte").then(({ data }) => setCorte(data)).catch(() => {});
  }, []);

  const load = async () => {
    const params = {};
    if (desde) params.desde = desde;
    if (hasta) params.hasta = hasta;
    if (modo === "historial") {
      if (cierreId) params.cierre_id = cierreId;
      else params.historial = true;
    }
    // Para /orders reutilizamos el mismo filtro cierre_id/historial
    const ordersParams = { ...params };
    const isAdminish = user?.role === "admin" || user?.role === "facturacion" || user?.role === "gerente_admin";
    const [s, o, oAll, ex, ct] = await Promise.all([
      api.get("/stats", { params }),
      api.get("/orders", { params: ordersParams }),
      // allOrders sirve para calcular atrasados; siempre en modo ACTUAL (sin cierre)
      api.get("/orders"),
      isAdminish ? api.get("/stats/extra", { params }).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      // Corte por timestamp del último cierre — solo tiene sentido en ACTUAL.
      modo === "actual" ? api.get("/stats/pedidos-corte").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
    ]).catch(() => [null]);
    // Un error de conexión NO se muestra como ventas en Q0.00: se conservan los datos anteriores y se avisa.
    if (!s || !s.data || typeof s.data !== "object" || !o || !Array.isArray(o.data?.items ?? o.data)) {
      setLoadError(true);
      return;
    }
    setLoadError(false);
    setStats(s.data);
    const ordersData = Array.isArray(o.data) ? o.data : (o.data.items || o.data);
    setOrders((ordersData || []).slice(0, 8));
    const allData = Array.isArray(oAll.data) ? oAll.data : (oAll.data.items || oAll.data);
    setAllOrders(allData || []);
    setExtra(ex.data);
    if (ct?.data) setCorte(ct.data);
  };

  useEffect(() => {
    load();
     
  }, [desde, hasta, modo, cierreId]);

  const clearRange = () => { setDesde(""); setHasta(""); };

  const by_state = stats?.by_state || {};

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {loadError && (
        <div className="rounded-md border-2 border-red-500 bg-red-50 p-4 flex flex-wrap items-center justify-between gap-3" data-testid="dashboard-error">
          <div className="text-sm text-red-800"><b>No se pudo conectar con el servidor.</b> {stats ? "Se muestran los últimos datos cargados; " : "Los valores no se muestran para no confundirlos con Q0.00; "}intente de nuevo.</div>
          <button onClick={load} className="h-10 px-4 rounded-md bg-red-600 text-white text-sm font-bold" data-testid="dashboard-reintentar">Reintentar</button>
        </div>
      )}
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-zinc-500 font-semibold">Panel principal</div>
          <h1 className="font-manrope font-black tracking-tight text-4xl sm:text-5xl mt-1">
            Hola, {user?.name?.split(" ")[0] || "equipo"}.
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">Resumen operativo de ventas, producción y entregas.</p>
        </div>
        <div className="flex flex-wrap gap-2">
        {user?.role === "admin" && (
          <button
            type="button"
            onClick={async () => {
              try {
                const token = localStorage.getItem("token");
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reports/conciliacion-dashboard.xlsx`, { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" });
                if (!res.ok) throw new Error();
                const blob = await res.blob();
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `conciliacion_dashboard_${new Date().toISOString().slice(0, 10)}.xlsx`;
                document.body.appendChild(a); a.click(); a.remove();
                setTimeout(() => URL.revokeObjectURL(a.href), 60000);
              } catch { alert("No se pudo generar la conciliación"); }
            }}
            className="inline-flex items-center gap-2 rounded-md border border-zinc-300 bg-white text-sm px-4 py-2 hover:bg-zinc-50 transition-colors duration-200"
            data-testid="dashboard-conciliacion-excel"
          >
            <ClipboardList className="w-4 h-4" /> Conciliar Dashboard vs Excel
          </button>
        )}
        <Link
          to="/pedidos"
          className="inline-flex items-center gap-2 rounded-md bg-zinc-900 text-white text-sm px-4 py-2 hover:bg-zinc-800 transition-colors duration-200"
          data-testid="dashboard-open-orders"
        >
          <ClipboardList className="w-4 h-4" /> Ir a Hoja de Pedidos
        </Link>
        </div>
      </header>

      {/* Date range + Actual/Historial toggle */}
      <Card className="p-4 border-zinc-200 rounded-md">
        <div className="flex flex-col md:flex-row md:items-end gap-3 flex-wrap">
          <div className="inline-flex rounded-md border border-zinc-200 overflow-hidden" data-testid="dash-modo-toggle">
            <button type="button" onClick={() => { setModo("actual"); setCierreId(""); }}
                    className={`px-3 py-2 text-xs uppercase tracking-widest font-black ${modo === "actual" ? "bg-black text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                    data-testid="dash-tab-actual">Actual</button>
            <button type="button" onClick={() => setModo("historial")}
                    className={`px-3 py-2 text-xs uppercase tracking-widest font-black ${modo === "historial" ? "bg-black text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}
                    data-testid="dash-tab-historial">Archivado</button>
          </div>
          {modo === "historial" && (
            <select value={cierreId} onChange={(e) => setCierreId(e.target.value)}
                    className="h-10 border border-zinc-200 rounded-md px-3 text-sm" data-testid="dash-hist-cierre">
              <option value="">Todos los cierres</option>
              {cierres.map((c) => (
                <option key={c.id} value={c.id}>#{c.numero_cierre} · {c.mes_key} · {c.desde} → {c.hasta}</option>
              ))}
            </select>
          )}
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 font-semibold">
            <CalendarIcon className="w-4 h-4" /> Rango
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 block">Desde</label>
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="dash-desde" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 block">Hasta</label>
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="dash-hasta" />
          </div>
          {(desde || hasta) && (
            <Button variant="outline" onClick={clearRange} data-testid="dash-clear">Limpiar</Button>
          )}
          <div className="text-xs text-zinc-500 md:ml-auto">
            {modo === "historial"
              ? (cierreId ? "Vista Archivado (cierre específico)" : "Vista Archivado (todos los cerrados)")
              : "Vista Actual (período abierto)"}
            {" · "}
            {desde || hasta ? `${desde || "…"} → ${hasta || "…"}` : "todo"}
          </div>
        </div>
      </Card>

      {/* NUEVO — Corte por timestamp exacto del último cierre. Regla:
          - Anteriores al cierre = pedidos creados ANTES del ejecutado_at y aún sin cierre_id (CxC).
          - Después del cierre  = pedidos creados DESPUÉS del ejecutado_at.
          - Totales             = suma. */}
      {modo === "actual" && corte && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="pedidos-corte-block">
          <Card className="p-5 border-2 border-amber-300 bg-amber-50/40 rounded-md" data-testid="corte-anteriores">
            <div className="text-[10px] uppercase tracking-widest text-amber-900 font-black">Pedidos anteriores al cierre</div>
            <div className="text-xs text-amber-800 mt-1">Creados antes del último cierre y aún activos</div>
            <div className="mt-2 font-manrope font-black tabular-nums text-4xl text-amber-900" data-testid="corte-anteriores-val">{corte.anteriores ?? 0}</div>
            {corte.ultimo_cierre_at && (
              <div className="text-xs text-amber-900 mt-1" data-testid="corte-anteriores-monto">
                No facturado al cierre: <b>{formatMoney(corte.anteriores_monto || 0)}</b>
              </div>
            )}
          </Card>
          <Card className="p-5 border-2 border-emerald-300 bg-emerald-50/40 rounded-md" data-testid="corte-despues">
            <div className="text-[10px] uppercase tracking-widest text-emerald-900 font-black">Pedidos después del cierre</div>
            <div className="text-xs text-emerald-800 mt-1">Creados después del último cierre — período abierto</div>
            <div className="mt-2 font-manrope font-black tabular-nums text-4xl text-emerald-900" data-testid="corte-despues-val">{corte.despues ?? 0}</div>
          </Card>
          <Card className="p-5 border-2 border-zinc-300 bg-zinc-50 rounded-md" data-testid="corte-total">
            <div className="text-[10px] uppercase tracking-widest text-zinc-700 font-black">Pedidos totales</div>
            <div className="text-xs text-zinc-600 mt-1">Anteriores + Después</div>
            <div className="mt-2 font-manrope font-black tabular-nums text-4xl text-zinc-900" data-testid="corte-total-val">{corte.total ?? 0}</div>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {extra && (
          <Link to="/cxc-facturada">
            <Stat label="CxC total" value={formatMoney(extra.cxc_total)} icon={Receipt} accent="bg-rose-600" sub={extra.cxc_arrastrado ? "Arrastrado en el último cierre" : "Saldo por cobrar (sin cierres aún)"} />
          </Link>
        )}
        <Stat label="Ventas del mes" value={formatMoney(stats?.total_ventas_mes)} icon={Wallet} accent="bg-blue-700" sub="Pedidos activos (Hoja)" />
        <Stat label="Ventas totales" value={formatMoney((Number(stats?.total_ventas_mes) || 0) + (Number(extra?.cxc_total) || 0))} icon={Wallet} accent="bg-indigo-600" sub="CxC + Ventas del mes" />
        <Stat label="Facturado" value={formatMoney(stats?.total_facturado)} icon={Wallet} accent="bg-[#10B981]" />
        <Stat label="Por facturar" value={formatMoney(stats?.total_por_facturar)} icon={Timer} accent="bg-[#F59E0B]" />
        <Stat label="N° de pagos" value={stats?.total_pagos ?? 0} icon={Wallet} accent="bg-emerald-800" />
        <Stat label="Pedidos totales" value={stats?.total_orders ?? "—"} icon={ClipboardList} accent="bg-[#002FA7]" />
        <Stat label="Artículos totales" value={stats?.total_items ?? "—"} icon={Package} accent="bg-zinc-900" />
      </div>

      {/* (removidas por decisión del usuario) — CxC anterior, Cobrado del mes actual,
          Cobrado del mes anteriores: se eliminaron del panel superior. */}

      {extra && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="kpi-extras">
          <Link to="/pedidos-cancelados">
            <Stat label="Pedidos cancelados" value={extra.cancelados_count} icon={XCircle} accent="bg-red-700" sub={formatMoney(extra.cancelados_monto)} />
          </Link>
          <Link to="/transportistas">
            <Stat label="Saldo transporte" value={formatMoney(extra.total_saldo_transporte)} icon={Truck} accent="bg-cyan-700" sub={`${(extra.transportistas_saldos || []).length} transportistas`} />
          </Link>
          <Link to="/cajas">
            <Stat
              label="Gastos por caja"
              value={formatMoney((extra.gastos_por_caja || []).reduce((s, c) => s + Number(c.egresos_periodo || 0), 0))}
              icon={Coins}
              accent="bg-amber-700"
              sub={`${(extra.gastos_por_caja || []).length} cajas`}
            />
          </Link>
        </div>
      )}

      {extra && (extra.gastos_por_caja || []).length > 0 && (
        <Card className="border-zinc-200 rounded-md overflow-hidden" data-testid="cajas-detail-card">
          <div className="p-5 border-b border-zinc-200">
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">Cajas</div>
            <div className="text-xl font-semibold mt-0.5">Egresos por caja</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-600">
                <tr className="text-left">
                  <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Caja</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold text-right">Saldo actual</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold text-right">Egresos periodo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {extra.gastos_por_caja.map((c) => (
                  <tr key={c.caja_id} className="hover:bg-zinc-50" data-testid={`caja-row-${c.caja_id}`}>
                    <td className="px-5 py-3 font-semibold">{c.nombre}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{formatMoney(c.saldo_actual)}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-red-600 font-semibold">{formatMoney(c.egresos_periodo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Ventas por vendedor (admin only) */}
      {user?.role === "admin" && (stats?.ventas_por_vendedor || []).length > 0 && (
        <Card className="border-zinc-200 rounded-md overflow-hidden" data-testid="ventas-vendedores-card">
          <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">Libro de vendedores</div>
              <div className="text-xl font-semibold mt-0.5">Ventas por vendedor</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-zinc-600">
                <tr className="text-left">
                  <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Vendedor</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold text-right">Pedidos</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold text-right">Artículos</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold text-right">Ventas (Q)</th>
                  <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold text-right">Facturado (Q)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {stats.ventas_por_vendedor.map((v) => (
                  <tr key={v.email} className="hover:bg-zinc-50" data-testid={`vendedor-row-${v.email}`}>
                    <td className="px-5 py-3">
                      <div className="font-semibold">{v.nombre}</div>
                      <div className="text-[10px] text-zinc-500">{v.email}</div>
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">{v.orders}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{v.items}</td>
                    <td className="px-5 py-3 text-right tabular-nums font-semibold">{formatMoney(v.ventas)}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-emerald-700 font-semibold">{formatMoney(v.facturado)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2 border-zinc-200 rounded-md">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">Últimos 7 días</div>
              <div className="text-xl font-semibold mt-0.5">Ingresos por día</div>
            </div>
          </div>
          <div className="w-full h-64">
            <ResponsiveContainer>
              <BarChart data={stats?.daily || []}>
                <CartesianGrid stroke="#e4e4e7" vertical={false} />
                <XAxis dataKey="date" stroke="#71717a" fontSize={11} />
                <YAxis stroke="#71717a" fontSize={11} />
                <Tooltip
                  formatter={(v) => formatMoney(v)}
                  contentStyle={{ border: "1px solid #e4e4e7", borderRadius: 6, fontSize: 12 }}
                />
                <Bar dataKey="revenue" fill="#002FA7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-5 border-zinc-200 rounded-md">
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">Pipeline</div>
          <div className="text-xl font-semibold mt-0.5 mb-4">Estado de pedidos</div>
          <ul className="space-y-2">
            {["creado", "fabricacion", "empaque", "entrega", "facturado", "pagado"].map((k) => (
              <li key={k} className="flex items-center justify-between text-sm">
                <StatusBadge status={k} />
                <span className="font-semibold tabular-nums">{by_state[k] ?? 0}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      {/* Alertas stock bajo inventario (Fase B) */}
      <StockBajoAlert />

      {/* Pedidos atrasados */}
      {(() => {
        const atrasados = allOrders
          .map((o) => ({ o, info: atrasoInfo(o) }))
          .filter((x) => x.info.atrasado)
          .sort((a, b) => b.info.dias - a.info.dias);
        if (atrasados.length === 0) return null;
        const ciudad = atrasados.filter((x) => x.info.color === "rojo").length;
        const dpto = atrasados.filter((x) => x.info.color === "amarillo").length;
        return (
          <Card className="border-red-200 rounded-md overflow-hidden bg-white" data-testid="atrasados-card">
            <div className="p-5 border-b border-zinc-200 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md grid place-items-center bg-red-600">
                  <AlertTriangle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">Alerta</div>
                  <div className="text-xl font-semibold mt-0.5">Pedidos atrasados</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Total</div>
                  <div className="font-manrope font-black text-3xl text-red-700" data-testid="atrasados-count">{atrasados.length}</div>
                </div>
                <div className="text-xs text-zinc-600 space-y-0.5">
                  <div><span className="inline-block w-2 h-2 rounded-full bg-red-600 mr-1" /> Ciudad &gt;1 día: <b>{ciudad}</b></div>
                  <div><span className="inline-block w-2 h-2 rounded-full bg-yellow-500 mr-1" /> Depto &gt;3 días: <b>{dpto}</b></div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const rows = [
                      ["Pedido", "Cliente", "Vendedor", "Ruta", "Fecha Hasta", "Dias atraso", "Total", "Estado entrega", "Nota"],
                      ...atrasados.map(({ o, info }) => [
                        o.numero, o.cliente_nombre || "", o.creado_por_nombre || "",
                        o.envio_tipo === "ciudad" ? "Ciudad" : "Departamento",
                        o.fecha_entrega_hasta || o.fecha_entrega || "",
                        info.dias, o.total || 0, o.estado_entrega || "pendiente",
                        [o.notas ? `Pedido: ${o.notas}` : "", o.notas_hoja || ""].filter(Boolean).join("\n"),
                      ]),
                    ];
                    const csv = rows.map((r) => r.map((c) => {
                      const s = String(c ?? "");
                      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                    }).join(",")).join("\n");
                    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `pedidos_atrasados_${new Date().toISOString().slice(0,10)}.csv`;
                    document.body.appendChild(a); a.click(); a.remove();
                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                  }}
                  className="ml-2 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs uppercase tracking-widest font-semibold rounded-md"
                  data-testid="atrasados-export-btn"
                >
                  Exportar
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-zinc-600">
                  <tr className="text-left">
                    <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Pedido</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Cliente</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Vendedor</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Ruta</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Fecha Hasta</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold text-right">Días de atraso</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold text-right">Total</th>
                    <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Nota</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {atrasados.slice(0, 20).map(({ o, info }) => (
                    <tr key={o.id} className={info.color === "rojo" ? "bg-red-50 hover:bg-red-100" : "bg-yellow-50 hover:bg-yellow-100"} data-testid={`atrasado-row-${o.numero}`}>
                      <td className="px-5 py-3">
                        <Link to={`/pedidos/${o.id}`} className="font-mono text-xs font-semibold text-[#002FA7] hover:underline">{o.numero}</Link>
                      </td>
                      <td className="px-5 py-3">
                        <div className="font-semibold">{o.cliente_nombre}</div>
                        {o.cliente_telefono && <div className="text-[11px] text-zinc-500">{o.cliente_telefono}{o.cliente_telefono2 ? <span className="text-zinc-400"> · alt {o.cliente_telefono2}</span> : null}</div>}
                      </td>
                      <td className="px-5 py-3 text-xs text-zinc-600">{o.creado_por_nombre || "—"}</td>
                      <td className="px-5 py-3 capitalize text-xs">
                        <span className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                          o.envio_tipo === "ciudad" ? "bg-blue-100 text-blue-900" : "bg-violet-100 text-violet-900"
                        }`}>
                          {o.envio_tipo === "ciudad" ? "Ciudad" : "Departamento"}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-xs">
                        {o.fecha_entrega_hasta || o.fecha_entrega || "—"}
                      </td>
                      <td className={`px-5 py-3 text-right font-bold ${info.color === "rojo" ? "text-red-700" : "text-yellow-700"}`}>
                        {info.dias} d
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums font-semibold">{formatMoney(o.total)}</td>
                      <td className="px-5 py-3 text-xs text-zinc-700 max-w-xs" data-testid={`atrasado-nota-${o.numero}`}>
                        {(o.notas_hoja || o.notas) ? (
                          <span className="italic text-zinc-700" title={o.notas_hoja || o.notas}>
                            {(o.notas_hoja || o.notas).slice(0, 80)}{((o.notas_hoja || o.notas).length > 80) ? "…" : ""}
                          </span>
                        ) : (
                          <span className="text-zinc-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {atrasados.length > 20 && (
              <div className="px-5 py-3 border-t border-zinc-100 text-xs text-zinc-500">
                Mostrando 20 de {atrasados.length}. Abre <Link to="/pedidos" className="text-[#002FA7] font-semibold hover:underline">Hoja de Pedidos</Link> para ver todos.
              </div>
            )}
          </Card>
        );
      })()}

      <Card className="border-zinc-200 rounded-md overflow-hidden">
        <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold">Actividad reciente</div>
            <div className="text-xl font-semibold mt-0.5">Últimos pedidos</div>
          </div>
          <Link to="/pedidos" className="text-xs uppercase tracking-widest font-semibold text-[#002FA7]">
            Ver todo →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr className="text-left">
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">N°</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Cliente</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Envío</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold">Estado</th>
                <th className="px-5 py-3 text-xs uppercase tracking-widest font-semibold text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {orders.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-5 py-10 text-center text-zinc-400 text-sm">
                    Aún no hay pedidos.
                  </td>
                </tr>
              )}
              {orders.map((o) => (
                <tr key={o.id} className="hover:bg-zinc-50">
                  <td className="px-5 py-3 font-mono text-xs">{o.numero}</td>
                  <td className="px-5 py-3">{o.cliente_nombre}</td>
                  <td className="px-5 py-3 text-zinc-600 capitalize">{o.envio_tipo || "—"}</td>
                  <td className="px-5 py-3"><StatusBadge status={o.estado} /></td>
                  <td className="px-5 py-3 text-right tabular-nums font-semibold">{formatMoney(o.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}


function StockBajoAlert() {
  const [alertas, setAlertas] = useState([]);
  useEffect(() => {
    api.get("/inventario/alertas-stock-bajo").then((r) => setAlertas(r.data || [])).catch(() => {});
  }, []);
  if (alertas.length === 0) return null;
  return (
    <Card className="border-orange-300 bg-orange-50 rounded-md p-5" data-testid="stock-bajo-alert">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-5 h-5 text-orange-700" />
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-orange-700 font-semibold">Alerta de inventario</div>
          <div className="text-lg font-semibold mt-0.5">
            {alertas.length} producto{alertas.length !== 1 ? "s" : ""} con stock bajo
          </div>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1.5">
            {alertas.slice(0, 9).map((a) => (
              <div key={a.id} className="text-xs bg-white rounded px-2 py-1 border border-orange-200 flex justify-between">
                <span className="font-semibold">{a.nombre}{a.tamano ? ` · ${a.tamano}` : ""}{a.color ? ` · ${a.color}` : ""}</span>
                <span className="text-orange-700 font-bold">{a.stock_actual} / min {a.stock_minimo}</span>
              </div>
            ))}
          </div>
          <Link to="/inventario" className="inline-block mt-3 text-xs text-orange-800 hover:underline font-semibold">Ir a Inventario →</Link>
        </div>
      </div>
    </Card>
  );
}

